/**
 * CancelInvoiceDialog — cancel-invoice feature.
 *
 * Christa had no way to cancel an invoice: a sent invoice can't be deleted,
 * an invoice with any payment history can't be deleted, and the status
 * dropdown never offered 'cancelled' as a target (InvoiceStatusSelect.tsx
 * ALLOWED_TRANSITIONS keeps `cancelled: []` deliberately -- cancel only ever
 * happens through THIS dialog, so it always confirms).
 *
 * Deliberately a pure, data-only component (no react-query / api
 * dependency), mirroring SendConfirmationDialog.tsx and
 * MergeConfirmationDialog.tsx -- the caller (InvoiceDetailPage, or
 * InvoiceListPage via the merge-blocked "Cancel invoice #N" buttons) owns
 * the mutation and passes state in as props.
 */
import { useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';

export interface CancelInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Displayed invoice number, e.g. "0642". */
  invoiceNumber: string;
  /**
   * Christa-lens redirect (2026-09-15): she may arrive at this dialog from
   * the merge-blocked "Cancel invoice #N" button with only a bare number in
   * front of her -- these three identify WHICH invoice she's about to
   * cancel before she commits.
   */
  projectName?: string | null;
  /** The Bill To client name. */
  clientName?: string | null;
  totalAmount?: number | null;
  /** Set when this invoice was already exported to QuickBooks (qbo_csv_exported_at). */
  qboExportedAt?: string | null;
  /** True while the cancel mutation is in flight — disables both actions. */
  pending?: boolean;
  /** Set when the last confirm attempt was refused or failed. Shown inline. */
  error?: { message: string } | null;
  /** Called with the optional reason text (undefined when left blank). */
  onConfirm: (reason: string | undefined) => void;
}

export function CancelInvoiceDialog({
  open,
  onOpenChange,
  invoiceNumber,
  projectName = null,
  clientName = null,
  totalAmount = null,
  qboExportedAt = null,
  pending = false,
  error = null,
  onConfirm,
}: CancelInvoiceDialogProps) {
  const [reason, setReason] = useState('');

  // Reset the reason field each time the dialog opens fresh, so a reason
  // typed for one invoice never leaks into the next one it's opened for.
  useEffect(() => {
    if (open) {
      setReason('');
    }
  }, [open]);

  const qboDateLabel = qboExportedAt
    ? new Date(qboExportedAt).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
      })
    : null;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="cancel-invoice-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel invoice #{invoiceNumber}?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div data-testid="cancel-invoice-details">
              {(projectName || clientName || totalAmount !== null) && (
                <ul className="mb-3 space-y-0.5 text-sm" data-testid="cancel-invoice-identity">
                  {projectName && (
                    <li data-testid="cancel-invoice-project">
                      <span className="font-medium">Project:</span> {projectName}
                    </li>
                  )}
                  {clientName && (
                    <li data-testid="cancel-invoice-client">
                      <span className="font-medium">Bill To:</span> {clientName}
                    </li>
                  )}
                  {totalAmount !== null && (
                    <li data-testid="cancel-invoice-total">
                      <span className="font-medium">Total:</span>{' '}
                      {`$${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    </li>
                  )}
                </ul>
              )}
              <p>It keeps its number and stays in your invoice list, marked Cancelled.</p>
              <p className="mt-1">It no longer counts as owing, and its payment reminders stop.</p>
              <p className="mt-1">
                The work on it shows as not yet invoiced{projectName ? ` on ${projectName}` : ''},
                so you can bill it on the right invoice. The project's scope isn't changed.
              </p>
              <p className="mt-1">It can't be reopened.</p>
              {qboDateLabel && (
                <p className="mt-3 rounded border border-amber-200 bg-amber-50 p-2 text-amber-800" data-testid="cancel-invoice-qbo-warning">
                  This invoice was exported to QuickBooks on {qboDateLabel}. Let
                  whoever keeps your books know, so they can void it there too.
                </p>
              )}
              <label className="mt-3 block" htmlFor="cancel-invoice-reason">
                <span className="text-sm font-medium">Reason (optional)</span>
                <Textarea
                  id="cancel-invoice-reason"
                  data-testid="cancel-invoice-reason"
                  className="mt-1"
                  rows={2}
                  maxLength={500}
                  value={reason}
                  disabled={pending}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. duplicate, wrong project, billed in error"
                />
              </label>
              {error && (
                <p role="alert" data-testid="cancel-invoice-error" className="mt-3 text-destructive">
                  {error.message}
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            data-testid="cancel-invoice-keep"
            aria-label={`Keep invoice ${invoiceNumber}`}
            disabled={pending}
          >
            Keep invoice
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="cancel-invoice-confirm"
            aria-label={`Confirm cancel invoice ${invoiceNumber}`}
            disabled={pending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={(e) => {
              // AlertDialogAction closes on click by default (Radix); we drive
              // closing ourselves via onConfirm/onOpenChange so the caller can
              // keep the dialog open on refusal, matching Send/Merge.
              e.preventDefault();
              onConfirm(reason.trim() ? reason.trim() : undefined);
            }}
          >
            {pending ? 'Cancelling…' : 'Cancel invoice'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
